import React from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheck,
  Calendar,
  Check,
  Headphones,
  Info
} from 'lucide-react';

const QuoteContractTermsSlide = ({ planType }) => {
  const isManaged = planType === "managed";

  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-8 xl:p-12 relative overflow-hidden font-sans">
        {/* Background Gradients */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[100px] -translate-y-1/2 -translate-x-1/4" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-indigo-100/40 rounded-full blur-[100px] translate-y-1/2 translate-x-1/4" />
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-60" />

        <div className="w-full max-w-6xl z-10 flex flex-col h-full justify-center relative">

            <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-bold uppercase tracking-wide mb-4 border border-blue-100">
                    <ShieldCheck className="w-3 h-3" />
                    <span>Simple Terms</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                    Contract <span className="text-blue-600">terms</span>
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
                    Simple, transparent terms designed to minimize risk and maximize alignment.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">

                {/* Card 1: 12-Month Term */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col relative overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all"
                >
                    <div className="flex items-center gap-4 mb-5">
                        <div className="w-12 h-12 bg-blue-50 rounded-xl border border-blue-100 flex items-center justify-center text-blue-600">
                            <Calendar className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-slate-900">12-month term</h3>
                            <p className="text-sm text-slate-400">Standard agreement</p>
                        </div>
                    </div>

                    <div className="space-y-3 flex-1">
                        <div className="flex items-start gap-2.5">
                            <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600">12-month commitment to ensure proper calibration and maximum results</span>
                        </div>
                        <div className="flex items-start gap-2.5">
                            <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600">Full white glove onboarding and ongoing support included</span>
                        </div>
                    </div>

                    <div className="mt-5 bg-blue-50 rounded-xl border border-blue-100 px-5 py-3.5 flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-700">Asha onboarding fee</span>
                        <span className="text-xl font-extrabold text-slate-900">$1,800</span>
                    </div>
                </motion.div>

                {/* Card 2: 18-Month Term */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="bg-white p-6 rounded-2xl shadow-sm border-2 border-blue-200 flex flex-col relative overflow-hidden hover:shadow-lg transition-all"
                >
                    {/* Recommended badge */}
                    <div className="absolute top-4 right-4">
                        <span className="bg-blue-600 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-lg">
                            RECOMMENDED
                        </span>
                    </div>

                    <div className="flex items-center gap-4 mb-5">
                        <div className="w-12 h-12 bg-blue-50 rounded-xl border border-blue-100 flex items-center justify-center text-blue-600">
                            <Calendar className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-slate-900">18-month term</h3>
                            <p className="text-sm text-slate-400">Extended partnership</p>
                        </div>
                    </div>

                    <div className="space-y-3 flex-1">
                        <div className="flex items-start gap-2.5">
                            <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600">18-month commitment for deeper integration and long-term ROI</span>
                        </div>
                        <div className="flex items-start gap-2.5">
                            <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600">Full white glove onboarding and ongoing support included</span>
                        </div>
                    </div>

                    <div className="mt-5 bg-blue-50 rounded-xl border border-blue-100 px-5 py-3.5 flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-700">Asha onboarding fee</span>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-400 line-through">$1,800</span>
                            <span className="text-xl font-extrabold text-blue-600">$0</span>
                        </div>
                    </div>
                </motion.div>

            </div>

            <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.22 }}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm px-6 py-4 mb-5"
            >
                <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0">
                        <Info className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                        <p className="text-sm font-bold text-slate-900 mb-2">Additional notes</p>
                        <ul className="space-y-2 text-sm text-slate-600 leading-relaxed">
                            <li className="flex items-start gap-2.5">
                                <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                                <span>SOTA Cloud Imaging is optional. Asha sponsors the first 3 months, then it is $159/mo, with a required $495 implementation fee and optional $995 historical data conversion.</span>
                            </li>
                            {isManaged && (
                                <li className="flex items-start gap-2.5">
                                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                                    <span>% based pricing uses a $60k/mo collections floor. That means a $1,800/mo retainer at the beginning of the month (3% of $60k) plus the $799/mo software fee, and any collections above $60k are billed at the same 3% and paid at the end of the month.</span>
                                </li>
                            )}
                            <li className="flex items-start gap-2.5">
                                <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                                <span>One month security deposit is paid while getting started and is applied to your last month of the contract term.</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </motion.div>

            {/* Support Note */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm px-6 py-4 flex items-center gap-4"
            >
                <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0">
                    <Headphones className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                    <p className="text-sm font-bold text-slate-900 mb-0.5">Complimentary support — always</p>
                    <p className="text-sm text-slate-500">
                        Whereas Dentrix charges <span className="font-bold text-slate-800">$150+ per incident</span> for support,
                        we provide support complimentary to ensure your office always has what it needs.
                    </p>
                </div>
            </motion.div>

        </div>
    </div>
  );
};

export default QuoteContractTermsSlide;
